"use client"
import { SearchBar } from '@/components/ui';
import {
  Badge,
  Box,
  Button,
  Flex,
  Select,
  Text,
  TextField
} from '@radix-ui/themes';
import { Filter } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export interface BlogFilters {
  searchTerm: string;
  selectedStatus: string;
  selectedAuthor: string;
  selectedBlogType: string;
  selectedDateRange: string;
  dateFrom?: string;
  dateTo?: string;
}

const statuses = ['All', 'Published', 'Draft', 'Scheduled'];
const dateRanges = ['All Time', 'Today', 'This Week', 'This Month', 'Last 3 Months'];
const blogTypes = ['All', 'Post', 'News', 'Thought & Leadership'];

interface BlogFiltersProps {
  onFiltersChange: (filters: BlogFilters) => void;
  onClearFilters?: () => void;
}

export default function BlogFilters({ onFiltersChange, onClearFilters }: BlogFiltersProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedAuthor, setSelectedAuthor] = useState('All');
  const [selectedDateRange, setSelectedDateRange] = useState('All Time');
  const [selectedBlogType, setSelectedBlogType] = useState('All');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [authors, setAuthors] = useState<string[]>(['All']);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Load authors from user management
  const loadAuthors = async () => {
    try {
      const response = await fetch('/api/admin/users');
      if (response.ok) {
        const data = await response.json();

        // Extract user names from the users array
        // Filter for active users with Author or Editor roles
        if (data.users && Array.isArray(data.users)) {
          const authorNames = data.users
            .filter((user: { refreshToken?: string; role: string; name: string }) =>
              user.refreshToken && // Active users have refresh tokens
              ['Author', 'Editor', 'Admin'].includes(user.role)
            )
            .map((user: { name: string }) => user.name)
            .filter((name: string) => name && name.trim().length > 0);

          // Add "All" option at the beginning
          const allAuthors = ['All', ...authorNames];
          setAuthors(allAuthors);
        } else {
          console.warn('Invalid users data structure:', data);
        }
      }
    } catch (err) {
      // Use default authors if API fails
      console.warn('Failed to load authors from API, using defaults:', err);
    }
  };

  // Load authors on component mount
  useEffect(() => {
    loadAuthors();
  }, []);

  // Memoize filters object to prevent unnecessary re-renders
  const memoizedFilters = useMemo(() => ({
    searchTerm,
    selectedStatus,
    selectedAuthor,
    selectedBlogType,
    selectedDateRange,
    ...(dateFrom && { dateFrom }),
    ...(dateTo && { dateTo }),
  }), [searchTerm, selectedStatus, selectedAuthor, selectedBlogType, selectedDateRange, dateFrom, dateTo]);

  // Notify parent component when filters change
  useEffect(() => {
    onFiltersChange(memoizedFilters);
  }, [memoizedFilters, onFiltersChange]);

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedStatus('All');
    setSelectedAuthor('All');
    setSelectedBlogType('All');
    setSelectedDateRange('All Time');
    setDateFrom('');
    setDateTo('');
    onClearFilters?.();
  };

  return (
    <Box className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <Box className="space-y-4">
        {/* Main Search */}
        <Flex align="center" gap="4">
          <Box className="flex-1 max-w-md">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search blogs by title, content, or tags..."
            />
          </Box>

          <Button
            variant="outline"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
        </Flex>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="grid grid-cols-1 md:grid-cols-14 gap-4 pt-4 border-t border-gray-200">
            {/* Status Filter */}
            <Box className='col-span-2'>
              <Text as="div" size="2" weight="medium" className="text-gray-700 mb-2">
                Status
              </Text>
              <Select.Root value={selectedStatus} onValueChange={setSelectedStatus}>
                <Select.Trigger className="w-full" />
                <Select.Content >
                  {statuses.map(status => (
                    <Select.Item key={status} value={status}>
                      {status}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>

            {/* Author Filter */}
            <Box className='col-span-2'>
              <Text as="div" size="2" weight="medium" className="text-gray-700 mb-2">
                Author
              </Text>
              <Select.Root value={selectedAuthor} onValueChange={setSelectedAuthor}>
                <Select.Trigger className="w-full" />
                <Select.Content>
                  {authors.map(author => (
                    <Select.Item key={author} value={author}>
                      {author}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>

            {/* Blog Type Filter */}
            <Box className='col-span-2'>
              <Text as="div" size="2" weight="medium" className="text-gray-700 mb-2">
                Type
              </Text>
              <Select.Root value={selectedBlogType} onValueChange={setSelectedBlogType}>
                <Select.Trigger className="w-full" />
                <Select.Content>
                  {blogTypes.map(type => (
                    <Select.Item key={type} value={type}>
                      {type}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>

            {/* Date Range Filter */}
            <Box className='col-span-3'>
              <Text as="div" size="2" weight="medium" className="text-gray-700 mb-2">
                Date Range
              </Text>
              <Select.Root value={selectedDateRange} onValueChange={setSelectedDateRange}>
                <Select.Trigger className="w-full" />
                <Select.Content>
                  {dateRanges.map(range => (
                    <Select.Item key={range} value={range}>
                      {range}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Box>

            {/* Custom Date Range */}
            <Box className='col-span-5'>
              <Text as="div" size="2" weight="medium" className="text-gray-700 mb-2">
                Custom Range
              </Text>
              <Flex gap="2">
                <TextField.Root
                  className="flex-1"
                  type="date"
                  placeholder="From"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
                <TextField.Root
                  className="flex-1"
                  type="date"
                  placeholder="To"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Flex>
            </Box>
          </div>
        )}

        {/* Active Filters Display */}
        {(selectedStatus !== 'All' || selectedAuthor !== 'All' || selectedBlogType !== 'All' || selectedDateRange !== 'All Time') && (
          <Flex align="center" gap="2" className="pt-4 border-t border-gray-200">
            <Text size="2" className="text-gray-500">Active filters:</Text>
            {selectedStatus !== 'All' && (
              <Badge variant="soft" color="blue">
                Status: {selectedStatus}
                <Button
                  variant="ghost"
                  size="1"
                  onClick={() => setSelectedStatus('All')}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </Button>
              </Badge>
            )}
            {selectedAuthor !== 'All' && (
              <Badge variant="soft" color="green">
                Author: {selectedAuthor}
                <Button
                  variant="ghost"
                  size="1"
                  onClick={() => setSelectedAuthor('All')}
                  className="ml-1 text-green-600 hover:text-green-800"
                >
                  ×
                </Button>
              </Badge>
            )}
            {selectedBlogType !== 'All' && (
              <Badge variant="soft" color="orange">
                Type: {selectedBlogType}
                <Button
                  variant="ghost"
                  size="1"
                  onClick={() => setSelectedBlogType('All')}
                  className="ml-1 text-orange-600 hover:text-orange-800"
                >
                  ×
                </Button>
              </Badge>
            )}
            {selectedDateRange !== 'All Time' && (
              <Badge variant="soft" color="purple">
                Date: {selectedDateRange}
                <Button
                  variant="ghost"
                  size="1"
                  onClick={() => setSelectedDateRange('All Time')}
                  className="ml-1 text-purple-600 hover:text-purple-800"
                >
                  ×
                </Button>
              </Badge>
            )}
            <Button
              variant="ghost"
              size="1"
              color="red"
              onClick={clearFilters}
            >
              Clear all
            </Button>
          </Flex>
        )}
      </Box>
    </Box>
  );
}
